/**
 * The empty `<style>` element the page shells emit for a draft preview, in the
 * same head position an activated workspace stylesheet `<link>` would take. The
 * position matters: the first `@layer` statement the browser parses fixes the
 * layer order for the document, so the preview CSS has to be parsed before the
 * Vite bundle for `tw-runtime` to outrank the bundle's `utilities` layer.
 */
export const PREVIEW_STYLESHEET_ELEMENT_ID = 'workspace-preview-stylesheet';

function createPlaceholder(): HTMLStyleElement {
  const style = document.createElement('style');
  style.id = PREVIEW_STYLESHEET_ELEMENT_ID;

  const firstStylesheetLink = document.head.querySelector('link[rel="stylesheet"]');

  if (firstStylesheetLink) {
    document.head.insertBefore(style, firstStylesheetLink);
  } else {
    document.head.append(style);
  }

  return style;
}

/**
 * Replaces the whole preview stylesheet with the CSS of one generation.
 *
 * The element is document-scoped rather than owned by a React tree, so it
 * survives an in-preview Inertia navigation and the next page's generation
 * simply overwrites it. The caller writes it in the same task that commits the
 * generation's transformed code, so no frame can show new code with old CSS.
 */
export function applyPreviewStylesheet(css: string): void {
  if (typeof document === 'undefined') {
    return;
  }

  const style =
    document.querySelector<HTMLStyleElement>(`style#${PREVIEW_STYLESHEET_ELEMENT_ID}`) ??
    createPlaceholder();

  style.textContent = css;
}
